/**
 * The arrangements every textbook draws, as data. Each carries the book's claim
 * twice: as the sentence the student reads, and as the numbers the tests hold
 * the bench to — so the bench is checked against the book rather than believed.
 *
 * Presets that reproduce a textbook figure use the arrow, since that is the
 * figure being reproduced. The lens, mirror or block sits at the rail's zero
 * mark, so an object's position on the rail IS its u.
 */
import { fitRail, emptyWorld } from './edit';
import { kindOf } from './registry';
import { ImagePoint, Part, Vec2, World } from './types';
import { fromAngle, rad, scale, sub } from './vec';
import { LensData } from './kinds/lens';
import { SphericalMirrorData } from './kinds/mirrors';
import { RAY_POINTS } from './kinds/sources';

export interface PresetExpect {
    /** The object's final image: New Cartesian v at the last element, the overall m. */
    image?: { v?: number; m?: number; kind: ImagePoint['kind']; erect?: boolean };
    /** Every ray of a parallel beam passes through this point after the lens. */
    focus?: Vec2;
    /** Of the ray box's ray, through the block: degrees, and cm. */
    deviation?: number;
    lateral?: number;
    tir?: boolean;
    /** sin i / sin r at the face the ray box's ray enters. */
    snell?: number;
    /** White light through a prism: violet's deviation less red's, degrees. */
    dispersion?: number;
}

export interface Preset {
    key: string;
    label: string;
    claim: string;
    build: () => World;
    expect: PresetExpect;
}

const ELEMENT_X = 0;

function part<D>(key: string, id: string, at: Vec2, over: Omit<Partial<Part<D>>, 'data'> & { data?: Partial<D> } = {}): Part {
    const base = kindOf(key).create(at, id) as unknown as Part;
    return { ...base, ...over, data: { ...base.data, ...(over.data ?? {}) } } as Part;
}

const world = (...parts: Part[]): World => fitRail({ ...emptyWorld(), parts });

const arrow = (u: number, height = 3): Part =>
    part('object', 'object-1', { x: ELEMENT_X + u, y: 0 }, { data: { form: 'arrow', length: height, traced: [1] } });

const lens = (data: Partial<LensData> = {}): Part => part<LensData>('lens', 'lens-1', { x: ELEMENT_X, y: 0 }, { data });

const mirror = (data: Partial<SphericalMirrorData>): Part =>
    part<SphericalMirrorData>('sphericalMirror', 'mirror-1', { x: ELEMENT_X, y: 0 }, { data });

/** A ray box `back` cm behind `hit`, aimed at it along `angleDeg`. */
const rayBoxAt = (hit: Vec2, angleDeg: number, back = 10): Part => {
    const dir = fromAngle(rad(angleDeg));
    return part('rayBox', 'rayBox-1', sub(hit, scale(dir, back)), { angle: rad(angleDeg) });
};

/** The midpoint of a 60°-or-other prism's first face, for a prism at `at` with apex up. */
const firstFaceMid = (at: Vec2, apexDeg: number, side: number): Vec2 => {
    const half = rad(apexDeg / 2);
    const a = side * Math.sin(half), b = side * Math.cos(half);
    return { x: at.x - a / 2, y: at.y + (2 * b) / 3 - b / 2 };
};

const lensCases: Preset[] = [
    {
        key: 'lens-beyond-2f', label: 'Convex lens — object beyond 2F',
        claim: 'u = −60 cm, f = +20 cm → v = +30 cm, m = −0.5: real, inverted, diminished, between F and 2F.',
        build: () => world(arrow(-60), lens()),
        expect: { image: { v: 30, m: -0.5, kind: 'real', erect: false } },
    },
    {
        key: 'lens-at-2f', label: 'Convex lens — object at 2F',
        claim: 'u = −40 cm → v = +40 cm, m = −1: real, inverted, the same size, at 2F.',
        build: () => world(arrow(-40), lens()),
        expect: { image: { v: 40, m: -1, kind: 'real', erect: false } },
    },
    {
        key: 'lens-between-f-2f', label: 'Convex lens — object between F and 2F',
        claim: 'u = −30 cm → v = +60 cm, m = −2: real, inverted, enlarged, beyond 2F.',
        build: () => world(arrow(-30), lens()),
        expect: { image: { v: 60, m: -2, kind: 'real', erect: false } },
    },
    {
        key: 'lens-at-f', label: 'Convex lens — object at F',
        claim: 'The rays leave the lens parallel: the image is at infinity.',
        build: () => world(arrow(-20), lens()),
        expect: { image: { kind: 'at-infinity' } },
    },
    {
        key: 'lens-inside-f', label: 'Convex lens — object inside F (magnifying glass)',
        claim: 'u = −10 cm → v = −20 cm, m = +2: virtual, erect, enlarged, on the same side as the object.',
        build: () => world(arrow(-10), lens()),
        expect: { image: { v: -20, m: 2, kind: 'virtual', erect: true } },
    },
    {
        key: 'concave-lens', label: 'Concave lens',
        claim: 'f = −20 cm, u = −30 cm → v = −12 cm, m = +0.4: virtual, erect, diminished, between F and O — for every object position.',
        build: () => world(arrow(-30), lens({ r1: -20, r2: 20 })),
        expect: { image: { v: -12, m: 0.4, kind: 'virtual', erect: true } },
    },
    {
        key: 'lensmaker', label: 'Plano-convex lens (lensmaker)',
        claim: 'A plano-convex lens of the same glass and |R| as the 20 cm biconvex has f = 40 cm: u = −60 cm → v = +120 cm, m = −2.',
        build: () => world(arrow(-60), lens({ r1: null, r2: -20 })),
        expect: { image: { v: 120, m: -2, kind: 'real', erect: false } },
    },
];

const concave = (r = 30): Part => mirror({ curve: 'concave', radius: r });

const mirrorCases: Preset[] = [
    {
        key: 'concave-beyond-c', label: 'Concave mirror — object beyond C',
        claim: 'f = −15 cm, u = −45 cm → v = −22.5 cm, m = −0.5: real, inverted, diminished, between F and C.',
        build: () => world(arrow(-45), concave()),
        expect: { image: { v: -22.5, m: -0.5, kind: 'real', erect: false } },
    },
    {
        key: 'concave-at-c', label: 'Concave mirror — object at C',
        claim: 'u = −30 cm → v = −30 cm, m = −1: real, inverted, the same size, at C.',
        build: () => world(arrow(-30), concave()),
        expect: { image: { v: -30, m: -1, kind: 'real', erect: false } },
    },
    {
        key: 'concave-between-c-f', label: 'Concave mirror — object between C and F',
        claim: 'u = −20 cm → v = −60 cm, m = −3: real, inverted, enlarged, beyond C.',
        build: () => world(arrow(-20), concave()),
        expect: { image: { v: -60, m: -3, kind: 'real', erect: false } },
    },
    {
        key: 'concave-at-f', label: 'Concave mirror — object at F',
        claim: 'The reflected rays are parallel: the image is at infinity.',
        build: () => world(arrow(-15), concave()),
        expect: { image: { kind: 'at-infinity' } },
    },
    {
        key: 'concave-inside-f', label: 'Concave mirror — object between F and P',
        claim: 'u = −10 cm → v = +30 cm, m = +3: virtual, erect, enlarged, behind the mirror.',
        build: () => world(arrow(-10), concave()),
        expect: { image: { v: 30, m: 3, kind: 'virtual', erect: true } },
    },
    {
        key: 'convex-mirror', label: 'Convex mirror',
        claim: 'f = +15 cm, u = −30 cm → v = +10 cm, m = +1/3: virtual, erect, diminished, behind the mirror.',
        build: () => world(arrow(-30), mirror({ curve: 'convex', radius: 30 })),
        expect: { image: { v: 10, m: 1 / 3, kind: 'virtual', erect: true } },
    },
    {
        key: 'plane-mirror', label: 'Plane mirror',
        claim: 'u = −20 cm → v = +20 cm, m = +1: virtual, erect, the same size, as far behind as the object is in front.',
        build: () => world(arrow(-20), part('planeMirror', 'mirror-1', { x: ELEMENT_X, y: 0 }, { mount: 'axis', angle: Math.PI })),
        expect: { image: { v: 20, m: 1, kind: 'virtual', erect: true } },
    },
];

const SLAB_AT = { x: 0, y: 0 };
const PRISM_AT = { x: 0, y: 0 };
const SEMICIRCLE_AT = { x: 0, y: 0 };
const slabPart = (): Part => part('slab', 'slab-1', SLAB_AT);

const blockCases: Preset[] = [
    {
        key: 'focal-length-distant-object', label: 'Focal length by a distant object',
        claim: 'Light from a distant object arrives parallel and is focused on the screen at F, 20 cm from the lens.',
        build: () => world(
            part('parallelBeam', 'beam-1', { x: ELEMENT_X - 60, y: 0 }),
            lens(),
            part('screen', 'screen-1', { x: ELEMENT_X + 20, y: 0 }),
        ),
        expect: { focus: { x: ELEMENT_X + 20, y: 0 } },
    },
    {
        key: 'glass-slab', label: 'Glass slab — lateral displacement',
        claim: 't = 4 cm, i = 45°, n = 1.5 → r = 28.1°; the ray comes out parallel to how it went in, shifted 1.32 cm sideways.',
        build: () => world(slabPart(), rayBoxAt({ x: SLAB_AT.x, y: 2 }, -45)),
        expect: { deviation: 0, lateral: 1.3166 },
    },
    {
        key: 'snell', label: "Snell's law",
        claim: 'For glass of n = 1.5, sin i / sin r = 1.5 at every angle of incidence.',
        build: () => world(slabPart(), rayBoxAt({ x: SLAB_AT.x, y: 2 }, -60)),
        expect: { snell: 1.5 },
    },
    {
        key: 'prism', label: 'Prism — angle of deviation',
        claim: 'A = 60°, n = 1.5, i = 40° → e = 58.5°, δ = i + e − A = 38.5°.',
        build: () => world(
            part('prism', 'prism-1', PRISM_AT),
            rayBoxAt(firstFaceMid(PRISM_AT, 60, 8), -30 + 40),
        ),
        expect: { deviation: 38.4656 },
    },
    {
        key: 'prism-minimum-deviation', label: 'Prism — minimum deviation',
        claim: 'A = 60°, n = 1.5: at i = e = 48.6° the deviation is least, δm = 37.2°, and n = sin((A + δm)/2) / sin(A/2).',
        build: () => world(
            part('prism', 'prism-1', PRISM_AT),
            rayBoxAt(firstFaceMid(PRISM_AT, 60, 8), -30 + 48.5904),
        ),
        expect: { deviation: 37.1808 },
    },
    {
        key: 'prism-dispersion', label: 'Prism — dispersion of white light',
        claim: 'White light splits in a prism: red is turned least and violet most, and between them they open out by about 3.5°.',
        build: () => world(
            part('prism', 'prism-1', PRISM_AT),
            part('rayBox', 'rayBox-1', sub(firstFaceMid(PRISM_AT, 60, 8), scale(fromAngle(rad(-30 + 48.5904)), 10)),
                { angle: rad(-30 + 48.5904), data: { white: true } }),
            // A screen far enough off to catch the colours a centimetre apart.
            part('screen', 'screen-1', { x: 20, y: 0 }),
        ),
        expect: { dispersion: 3.4909 },
    },
    {
        key: 'semicircle-below-critical', label: 'Semicircular block — below the critical angle',
        claim: 'n = 1.5, critical angle 41.8°. At 35° the ray leaves the flat face, refracted away from the normal.',
        build: () => world(part('semicircle', 'semicircle-1', SEMICIRCLE_AT), radialRayBox(35)),
        expect: { tir: false },
    },
    {
        key: 'semicircle-beyond-critical', label: 'Semicircular block — beyond the critical angle',
        claim: 'n = 1.5, critical angle 41.8°. At 45° the ray is totally internally reflected.',
        build: () => world(part('semicircle', 'semicircle-1', SEMICIRCLE_AT), radialRayBox(45)),
        expect: { tir: true },
    },
    {
        key: 'totally-reflecting-prism', label: 'Totally reflecting prism',
        claim: 'A 45°–45°–90° prism turns a beam through 90°: it enters and leaves square to the faces and reflects totally inside.',
        build: () => world(
            part('prism', 'prism-1', PRISM_AT, { data: { apex: 90 } }),
            rayBoxAt(firstFaceMid(PRISM_AT, 90, 8), -45, 6),
        ),
        expect: { deviation: 90, tir: true },
    },
];

/** A ray box aimed at the centre of a semicircular block's flat face, `i` from the normal. */
function radialRayBox(iDeg: number): Part {
    const i = rad(iDeg);
    const c = SEMICIRCLE_AT;
    const from = { x: c.x - 8 * Math.sin(i), y: c.y + 8 * Math.cos(i) };
    const angle = Math.atan2(c.y - from.y, c.x - from.x);
    return part('rayBox', 'rayBox-1', from, { angle });
}

export const PRESETS: Preset[] = [...lensCases, ...mirrorCases, ...blockCases];

export const presetByKey = (key: string): Preset | undefined => PRESETS.find(p => p.key === key);
/** The bench opens on the textbook's first figure: a convex lens at the zero
 *  mark and the object at its 2F, imaged at the other 2F, the same size —
 *  drawn as the gradient rod, traced from three points, rather than the
 *  arrow, so the first thing on the bench shows that the image is the object
 *  turned over end to end, not just a smaller or larger arrow. The preset itself keeps the textbook's
 *  arrow, as the others do. */
export const openingBench = (): Preset => {
    const figure = presetByKey('lens-at-2f') as Preset;
    return {
        ...figure,
        build: () => {
            const w = figure.build();
            return {
                ...w,
                // Rays from three points of the rod — its two ends and its middle —
                // so the image is seen to be built point by point.
                parts: w.parts.map(p => (p.kind === 'object'
                    ? { ...p, data: { ...p.data, form: 'rod', rayPoints: '3', traced: RAY_POINTS['3'] } }
                    : p)),
            };
        },
    };
};
